/**
 * AdminPromoCodesPage — /admin/promo-codes
 *
 * Item 2 (Promo Codes) Commit A — admin surface for creating, listing,
 * editing, and deactivating platform-wide promo codes. All discounts
 * are commission-clamped (the booking-time enforcement lives in
 * `bookings.py` and lands in Commit B under human review).
 *
 * Design notes:
 *   - No inline hex colors in JSX (Family-L invariant). All colors flow
 *     through the Tailwind design tokens (`pp-*`) + shadcn primitives.
 *   - Uppercase-only visual for code display (server canonicalizes too).
 *   - Discount type + value + code are IMMUTABLE post-create — the
 *     patch surface only exposes `max_redemptions`, `per_user_limit`,
 *     `expires_at`, `starts_at`, `first_booking_only`, and `status`.
 *
 * Nothing here writes a redemption row; that path only opens when
 * bookings.py wires the atomic guard (Commit B). Analytics on
 * absorbed-vs-requested lands in a later dispatch alongside the
 * dedicated admin analytics tile.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Loader2, Ticket, PowerOff, Pencil } from "lucide-react";

import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatDiscount(row) {
  if (row.discount_type === "percent") {
    return `${row.discount_value}% off`;
  }
  const dollars = (row.discount_value / 100).toFixed(2);
  return `$${dollars} off`;
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    year: "numeric", month: "short", day: "numeric",
  });
}

function toLocalDateTimeInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => `${n}`.padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

function fromLocalDateTimeInput(str) {
  if (!str) return null;
  const d = new Date(str);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

// ---------------------------------------------------------------------------
// Create promo dialog
// ---------------------------------------------------------------------------
function CreatePromoDialog({ open, onOpenChange, onCreated }) {
  const [code, setCode] = useState("");
  const [discountType, setDiscountType] = useState("percent");
  const [discountValue, setDiscountValue] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [maxRedemptions, setMaxRedemptions] = useState("");
  const [perUserLimit, setPerUserLimit] = useState("1");
  const [firstBookingOnly, setFirstBookingOnly] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setCode(""); setDiscountType("percent"); setDiscountValue("");
    setExpiresAt(""); setStartsAt(""); setMaxRedemptions("");
    setPerUserLimit("1"); setFirstBookingOnly(false);
  };

  const submit = async () => {
    if (!code.trim()) { toast.error("Code is required."); return; }
    if (!discountValue || Number(discountValue) <= 0) {
      toast.error("Discount value must be > 0."); return;
    }
    if (!expiresAt) { toast.error("Expires-at is required."); return; }
    setSubmitting(true);
    try {
      const body = {
        code: code.trim(),
        discount_type: discountType,
        discount_value: Number(discountValue),
        expires_at: fromLocalDateTimeInput(expiresAt),
        per_user_limit: Number(perUserLimit || 1),
        first_booking_only: firstBookingOnly,
      };
      if (startsAt) body.starts_at = fromLocalDateTimeInput(startsAt);
      if (maxRedemptions) body.max_redemptions = Number(maxRedemptions);
      const { data } = await api.post("/admin/promo-codes", body);
      toast.success(`Promo code ${data.code} created.`);
      reset();
      onOpenChange(false);
      onCreated(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to create promo code.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="admin-promo-create-dialog">
        <DialogHeader>
          <DialogTitle>New promo code</DialogTitle>
          <DialogDescription>
            Code + discount type + value + expiry are locked once created.
            All discounts are commission-clamped at booking time.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid gap-1.5">
            <Label htmlFor="promo-code">Code</Label>
            <Input
              id="promo-code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="SUMMER25"
              maxLength={64}
              data-testid="admin-promo-create-code"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Discount type</Label>
              <Select value={discountType} onValueChange={setDiscountType}>
                <SelectTrigger data-testid="admin-promo-create-discount-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Percent</SelectItem>
                  <SelectItem value="fixed">Fixed (cents)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="promo-discount-value">
                {discountType === "percent" ? "Percent (0–100)" : "Cents (int)"}
              </Label>
              <Input
                id="promo-discount-value"
                type="number"
                value={discountValue}
                onChange={(e) => setDiscountValue(e.target.value)}
                min={1}
                data-testid="admin-promo-create-discount-value"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="promo-starts-at">Starts at (optional)</Label>
              <Input
                id="promo-starts-at"
                type="datetime-local"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
                data-testid="admin-promo-create-starts-at"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="promo-expires-at">Expires at</Label>
              <Input
                id="promo-expires-at"
                type="datetime-local"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                data-testid="admin-promo-create-expires-at"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="promo-max-redemptions">Max redemptions (blank = unlimited)</Label>
              <Input
                id="promo-max-redemptions"
                type="number"
                value={maxRedemptions}
                onChange={(e) => setMaxRedemptions(e.target.value)}
                min={1}
                data-testid="admin-promo-create-max-redemptions"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="promo-per-user-limit">Per-user limit</Label>
              <Input
                id="promo-per-user-limit"
                type="number"
                value={perUserLimit}
                onChange={(e) => setPerUserLimit(e.target.value)}
                min={1}
                data-testid="admin-promo-create-per-user-limit"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="promo-first-booking-only"
              checked={firstBookingOnly}
              onCheckedChange={setFirstBookingOnly}
              data-testid="admin-promo-create-first-booking-only"
            />
            <Label htmlFor="promo-first-booking-only" className="cursor-pointer">
              First booking only
            </Label>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="admin-promo-create-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            data-testid="admin-promo-create-submit"
          >
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Edit promo dialog (limited fields)
// ---------------------------------------------------------------------------
function EditPromoDialog({ row, open, onOpenChange, onSaved }) {
  const [maxRedemptions, setMaxRedemptions] = useState("");
  const [perUserLimit, setPerUserLimit] = useState("1");
  const [expiresAt, setExpiresAt] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [firstBookingOnly, setFirstBookingOnly] = useState(false);
  const [status, setStatus] = useState("active");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (row) {
      setMaxRedemptions(row.max_redemptions == null ? "" : String(row.max_redemptions));
      setPerUserLimit(String(row.per_user_limit ?? 1));
      setExpiresAt(toLocalDateTimeInput(row.expires_at));
      setStartsAt(toLocalDateTimeInput(row.starts_at));
      setFirstBookingOnly(!!row.first_booking_only);
      setStatus(row.status === "disabled" ? "disabled" : "active");
    }
  }, [row]);

  if (!row) return null;

  const submit = async () => {
    setSubmitting(true);
    try {
      const body = {
        per_user_limit: Number(perUserLimit || 1),
        first_booking_only: firstBookingOnly,
        status,
      };
      if (maxRedemptions) body.max_redemptions = Number(maxRedemptions);
      if (expiresAt) body.expires_at = fromLocalDateTimeInput(expiresAt);
      if (startsAt) body.starts_at = fromLocalDateTimeInput(startsAt);
      const { data } = await api.patch(`/admin/promo-codes/${row.id}`, body);
      toast.success(`Promo ${data.code} updated.`);
      onOpenChange(false);
      onSaved(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Update failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="admin-promo-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit {row.code}</DialogTitle>
          <DialogDescription>
            Discount type + value + code are locked; edits are limited to
            caps, dates, and status.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Max redemptions</Label>
              <Input
                type="number"
                value={maxRedemptions}
                onChange={(e) => setMaxRedemptions(e.target.value)}
                min={1}
                data-testid="admin-promo-edit-max-redemptions"
              />
            </div>
            <div className="grid gap-1.5">
              <Label>Per-user limit</Label>
              <Input
                type="number"
                value={perUserLimit}
                onChange={(e) => setPerUserLimit(e.target.value)}
                min={1}
                data-testid="admin-promo-edit-per-user-limit"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Starts at</Label>
              <Input
                type="datetime-local"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
                data-testid="admin-promo-edit-starts-at"
              />
            </div>
            <div className="grid gap-1.5">
              <Label>Expires at</Label>
              <Input
                type="datetime-local"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                data-testid="admin-promo-edit-expires-at"
              />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="admin-promo-edit-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="disabled">Disabled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={firstBookingOnly}
              onCheckedChange={setFirstBookingOnly}
              data-testid="admin-promo-edit-first-booking-only"
            />
            <Label className="cursor-pointer">First booking only</Label>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="admin-promo-edit-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            data-testid="admin-promo-edit-submit"
          >
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function AdminPromoCodesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (statusFilter !== "all") params.status = statusFilter;
      if (search.trim()) params.q = search.trim();
      const { data } = await api.get("/admin/promo-codes", { params });
      setRows(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load promo codes.");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDeactivate = async (row) => {
    if (row.status === "disabled") return;
    if (!window.confirm(`Deactivate promo code ${row.code}?`)) return;
    try {
      await api.delete(`/admin/promo-codes/${row.id}`);
      toast.success(`${row.code} deactivated.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Deactivate failed.");
    }
  };

  const emptyMsg = useMemo(() => {
    if (loading) return null;
    if (rows.length === 0) return "No promo codes yet. Click ‘New promo code’ to add one.";
    return null;
  }, [loading, rows]);

  return (
    <div className="space-y-6" data-testid="admin-promo-codes-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Ticket className="h-6 w-6 text-pp-orange" aria-hidden="true" />
          <div>
            <h2 className="text-2xl font-semibold text-pp-text">Promo codes</h2>
            <p className="text-sm text-pp-muted mt-1">
              Platform-funded discounts. Booking-time enforcement clamps
              absorbed discount at the partner commission.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="admin-promo-refresh"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            onClick={() => setCreateOpen(true)}
            data-testid="admin-promo-new-btn"
          >
            New promo code
          </Button>
        </div>
      </div>

      <div className="flex items-end gap-3 flex-wrap">
        <div className="grid gap-1.5">
          <Label>Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px]" data-testid="admin-promo-filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="disabled">Disabled</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5 flex-1 min-w-[200px]">
          <Label htmlFor="promo-search">Search code</Label>
          <Input
            id="promo-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="SUMMER"
            data-testid="admin-promo-search"
          />
        </div>
      </div>

      <div
        className="rounded-lg border border-pp-border bg-pp-surface overflow-x-auto"
        data-testid="admin-promo-table-wrap"
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Discount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Redemptions</TableHead>
              <TableHead>Per-user</TableHead>
              <TableHead>First-only</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="text-center py-8 text-pp-muted"
                  data-testid="admin-promo-empty"
                >
                  {emptyMsg}
                </TableCell>
              </TableRow>
            )}
            {!loading && rows.map((row) => (
              <TableRow key={row.id} data-testid={`admin-promo-row-${row.code}`}>
                <TableCell className="font-mono font-semibold">{row.code}</TableCell>
                <TableCell>{formatDiscount(row)}</TableCell>
                <TableCell>
                  <span
                    className={
                      row.status === "active"
                        ? "text-pp-success"
                        : row.status === "disabled"
                          ? "text-pp-muted"
                          : "text-pp-warning"
                    }
                  >
                    {row.status}
                  </span>
                </TableCell>
                <TableCell>
                  {row.redemption_count}
                  {row.max_redemptions != null ? ` / ${row.max_redemptions}` : ""}
                </TableCell>
                <TableCell>{row.per_user_limit}</TableCell>
                <TableCell>{row.first_booking_only ? "Yes" : "No"}</TableCell>
                <TableCell>{formatDate(row.expires_at)}</TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditRow(row)}
                    data-testid={`admin-promo-edit-${row.code}`}
                    aria-label={`Edit promo ${row.code}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeactivate(row)}
                    disabled={row.status === "disabled"}
                    data-testid={`admin-promo-deactivate-${row.code}`}
                    aria-label={`Deactivate promo ${row.code}`}
                  >
                    <PowerOff className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <CreatePromoDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => load()}
      />
      <EditPromoDialog
        row={editRow}
        open={editRow !== null}
        onOpenChange={(open) => { if (!open) setEditRow(null); }}
        onSaved={() => { setEditRow(null); load(); }}
      />
    </div>
  );
}
